import { useCallback, useEffect, useMemo,useRef } from 'react'
import { useAppDispatch, useAppSelector } from 'store'

import {
  fetchTranslationProgress,
  selectTranslationProgress
} from 'services/translationsSlice'
import { TRANSLATION_STATUS } from 'types/translations'

interface UseTranslationProgressProps {
  translationId?: string
  enabled?: boolean
  interval?: number
  maxPollTime?: number
}

export const useTranslationProgressPolling = ({
  translationId,
  enabled = true,
  interval = 3500,
  maxPollTime = 300000
}: UseTranslationProgressProps) => {
  const dispatch = useAppDispatch()
  const translationProgress = useAppSelector(state =>
    selectTranslationProgress(state, translationId)
  )
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number>(Date.now())

  const isInProgress =
    translationProgress &&
    [
      TRANSLATION_STATUS.pending,
      TRANSLATION_STATUS.validating,
      TRANSLATION_STATUS.translating,
      TRANSLATION_STATUS.copying_files,
      TRANSLATION_STATUS.creating_product,
      TRANSLATION_STATUS.translating_assets,
      TRANSLATION_STATUS.finalizing
    ].includes(translationProgress.status)

  const isCompleted =
    translationProgress?.status === TRANSLATION_STATUS.completed
  const isFailed = translationProgress?.status === TRANSLATION_STATUS.failed

  const shouldPoll = useMemo(() => {
    if (!enabled || !translationId) return false

    if (!translationProgress) return true

    if (
      translationProgress.status === 'completed' ||
      translationProgress.status === 'failed'
    ) {
      return false
    }

    return [
      'pending',
      'validating',
      'translating',
      'copying_files',
      'creating_product',
      'translating_assets',
      'finalizing'
    ].includes(translationProgress.status)
  }, [enabled, translationId, translationProgress])

  const pollProgress = useCallback(async () => {
    if (!translationId) return

    try {
      await dispatch(fetchTranslationProgress(translationId))
    } catch (error) {
      console.error('Failed to fetch translation progress:', error)
      // Don't throw the error, just log it to prevent component crashes
    }
  }, [translationId, dispatch])

  useEffect(() => {
    try {
      if (shouldPoll) {
        startTimeRef.current = Date.now()
        pollProgress()

        intervalRef.current = setInterval(() => {
          try {
            if (Date.now() - startTimeRef.current > maxPollTime) {
              console.warn('Translation polling timeout reached')
              if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
              }

              return
            }

            pollProgress()
          } catch (error) {
            console.error('Error in polling interval:', error)
          }
        }, interval)
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    } catch (error) {
      console.error('Error in useTranslationProgressPolling useEffect:', error)
    }

    return () => {
      try {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      } catch (error) {
        console.error('Error cleaning up polling interval:', error)
      }
    }
  }, [
    shouldPoll,
    translationId,
    interval,
    maxPollTime,
    pollProgress,
    translationProgress,
    dispatch
  ])

  return {
    translationProgress,
    isInProgress,
    isCompleted,
    isFailed,
    isPolling: shouldPoll
  }
}
